import React from "react";
import { cn } from "@/lib/utils";
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Bell,
  Check,
  CheckCircle,
  ChevronRight,
  Clock,
  Gift,
  HelpCircle,
  Info,
  Lightbulb,
  Link,
  Rocket,
  Settings,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  Trophy,
  Zap,
  type LucideIcon,
} from "lucide-react";

// Whitelist of allowed icons for HubUpdates markdown content
// This prevents importing the entire lucide-react library (~175KB)
// Add new icons here as needed for HubUpdates content
const ALLOWED_ICONS: Record<string, LucideIcon> = {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Bell,
  Check,
  CheckCircle,
  ChevronRight,
  Clock,
  Gift,
  HelpCircle,
  Info,
  Lightbulb,
  Link,
  Rocket,
  Settings,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  Trophy,
  Zap,
};

interface IconSectionProps {
  icon?: string;
  variant?: "default" | "highlight" | "warning" | "success";
  children: React.ReactNode;
}

const ICON_VARIANT_STYLES = {
  default: {
    container: "bg-cede-bg-component/40",
    icon: "text-cede-primary",
  },
  highlight: {
    container: "bg-gradient-to-br from-cede-primary/20 to-cede-primary/5",
    icon: "text-cede-primary",
  },
  warning: {
    container: "bg-gradient-to-br from-yellow-500/20 to-yellow-500/5",
    icon: "text-yellow-500",
  },
  success: {
    container: "bg-gradient-to-br from-green-500/20 to-green-500/5",
    icon: "text-green-500",
  },
};

export const IconSection = ({ icon, variant = "default", children }: IconSectionProps) => {
  // Get the icon component from the whitelist (tree-shakeable)
  const IconComponent = icon ? ALLOWED_ICONS[icon] : null;
  const styles = ICON_VARIANT_STYLES[variant];

  if (!IconComponent) {
    // If no icon specified, render with background and border but without icon
    return (
      <div className={cn("p-3 rounded-lg mb-2 mt-2 border border-cede-border-container/50", styles.container)}>
        <div className="[&_p]:leading-snug [&_p]:my-0 [&_p:first-child]:mt-0">{children}</div>
      </div>
    );
  }

  return (
    <div className={cn("flex gap-3 p-3 rounded-lg mb-2 mt-2 border border-cede-border-container/50", styles.container)}>
      <div className="shrink-0 flex items-start pt-[2px]">
        <IconComponent className={cn("w-5 h-5", styles.icon)} />
      </div>
      <div className="flex-1 min-w-0 [&_p]:leading-snug [&_p]:my-0 [&_p:first-child]:mt-0">{children}</div>
    </div>
  );
};
